import { useEffect, useState, useCallback } from "react";
import api from "@/api/client";
import useWebSocket from "@/hooks/useWebSocket";
import { useAuth } from "@/contexts/AuthContext";
import { Megaphone, X, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Global in-app announcement banner.
 *
 * Fetches `GET /announcements/unread` on mount (any active broadcast a signed-in
 * customer/driver hasn't dismissed) and also listens for `announcement.new`
 * WebSocket events on the user's role topic so a broadcast sent while the app
 * is open pops in instantly.
 *
 * Multi-channel delivery guarantees users see admin broadcasts even without
 * granting browser push permission.
 */
export default function AnnouncementBanner() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/announcements/unread");
      setItems(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  useWebSocket({
    onMessage: (msg) => {
      if (msg?.event === "announcement.new" && msg.topic?.startsWith("role:")) {
        const p = msg.data || {};
        setItems((prev) => {
          if (prev.some((x) => x.id === p.id)) return prev;
          return [{ ...p }, ...prev];
        });
        setExpanded(true);
      }
    },
  });

  if (!user || items.length === 0) return null;

  const dismiss = async (id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
    try { await api.post(`/announcements/${id}/dismiss`); } catch { /* ignore */ }
  };

  const openLink = (link) => {
    if (!link) return;
    try {
      if (link.startsWith("http")) window.open(link, "_blank");
      else window.location.href = link;
    } catch { /* ignore */ }
  };

  const primary = items[0];

  return (
    <div
      className="sticky top-0 z-[55] w-full animate-fade-up"
      data-testid="announcement-banner"
      style={{
        background: "linear-gradient(135deg, #E05A3D 0%, #C4472F 100%)",
        boxShadow: "0 4px 16px rgba(224,90,61,0.35)",
      }}
    >
      <div className="max-w-6xl mx-auto px-3 sm:px-5 py-2.5 flex items-start gap-3 text-white">
        <div className="shrink-0 w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center mt-0.5">
          <Megaphone size={18}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-black text-sm sm:text-base leading-tight" style={{fontFamily:'Cairo'}}>
              {primary.title}
            </span>
            {items.length > 1 && (
              <span className="text-[10px] font-black bg-white/25 px-2 py-0.5 rounded-full">
                +{items.length - 1}
              </span>
            )}
          </div>
          {expanded && (
            <div className="mt-1 text-xs sm:text-sm text-white/95 leading-relaxed whitespace-pre-wrap" data-testid="announcement-body">
              {primary.body}
              {primary.link && (
                <button
                  onClick={() => openLink(primary.link)}
                  className="ml-2 underline underline-offset-2 font-black hover:text-white"
                  data-testid="announcement-link"
                >
                  عرض التفاصيل
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/85 hover:bg-white/15 transition"
            aria-label={expanded ? "طي" : "توسيع"}
            data-testid="announcement-toggle"
          >
            {expanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
          </button>
          <button
            onClick={() => dismiss(primary.id)}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/15 transition"
            aria-label="إغلاق"
            data-testid={`announcement-dismiss-${primary.id}`}
          >
            <X size={16}/>
          </button>
        </div>
      </div>
    </div>
  );
}
